import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { shopifyUpdateOrder } from '../../../../actions/tools/ShopifyActions';
import { isSpecialist } from '../../../../extractFromState';
import { getGeoDataByType, GeoTypes } from '../../../../constants/geodata';
import MDSpinner from 'react-md-spinner';
import {
  selectSelectedToolDomains,
  getOrderModificationsAreAllowed,
  getSelectedOrder,
} from '../../../../selectors/tools';
import { oneBtnPopupCreator } from '../../../popups/ConfirmPopup/helper';
import get from 'lodash/get';
import { cleanUndefinedPropertiesFromObject } from '../../../../util/util';
import ChangeAddressForm from '../../common/ChangeAddressForm/ChangeAddressForm';
import * as toolSelectors from '../../../../selectors/tools';
import { ErrorMsg, LabelWrapper, ErrorText, Button, Page, ButtonWrapper } from './ShopifyShippingAddress.styles';
import { handleError } from 'services/ErrorService/ErrorService';

class ShopifyShippingAddress extends Component {
  constructor(props) {
    super(props);

    const shippingAddress = get(props, 'selectedOrder.shipping_address', {});

    this.state = {
      name: shippingAddress.name || '',
      address1: shippingAddress.address1 || '',
      address2: shippingAddress.address2 || '',
      city: shippingAddress.city || '',
      province_code: shippingAddress.province_code || '',
      country_code: shippingAddress.country_code || '',
      province: shippingAddress.province || '',
      zip: shippingAddress.zip || '',
      errors: {},
      readOnlyRight: true,
      processing: false,
    };
  }

  async componentDidMount() {
    this.setState({
      readOnlyRight: this.checkOnReadOnlyRight(),
    });
  }

  update = (data) => {
    const errors = { ...this.state.error };
    Object.keys(data).forEach((key) => delete errors[key]);
    this.setState({ errors, ...data });
  };

  formFields = [
    {
      field: 'name',
      label: 'Full name',
      type: 'input',
    },
    {
      field: 'country_code',
      label: 'Country',
      type: 'select',
      items: getGeoDataByType(GeoTypes.COUNTRY_LIST).map((item) => ({ value: item.code, label: item.name })),
      cleanBefore: ['province', 'province_code'],
    },
    {
      field: 'city',
      label: 'City',
      type: 'input',
    },
    {
      field: 'province_code',
      label: 'State/Region',
      type: 'select',
      items: getGeoDataByType(GeoTypes.PROVINCE_LIST).map((item) => ({ value: item.code, label: item.name })),
      show: { country_code: 'US' },
      cleanBefore: ['province'],
    },
    {
      field: 'province',
      label: 'Province',
      type: 'input',
      hide: { country_code: 'US' },
      cleanBefore: ['province_code'],
    },
    {
      field: 'address1',
      label: 'Address line 1',
      type: 'input',
    },
    {
      field: 'address2',
      label: 'Address line 2',
      type: 'input',
    },
    {
      field: 'zip',
      label: 'ZIP',
      type: 'input',
    },
  ];

  validate = (value) => {
    const errors = {};
    if (!value.name) {
      errors.name = 'Empty name field';
    }

    if (!value.address1) {
      errors.address1 = 'Empty address field';
    }

    if (!value.city) {
      errors.city = 'Empty city field';
    }

    if (!value.province_code && !value.province) {
      errors.province_code = 'Empty state field';
      errors.province = 'Empty province field';
    }

    if (!value.zip) {
      errors.zip = 'Empty zipcode field';
    }

    this.setState({ errors });

    return Object.keys(errors).length === 0;
  };

  async updateShippingAddress(domain, orderId) {
    const newShippingAddress = cleanUndefinedPropertiesFromObject({
      name: this.state.name,
      address1: this.state.address1,
      address2: this.state.address2,
      country_code: this.state.country_code,
      city: this.state.city,
      province_code: this.state.province_code,
      province: this.state.province,
      zip: this.state.zip,
    });

    const valid = this.validate(newShippingAddress);
    if (valid) {
      this.setState({ processing: true });

      try {
        await this.props.updateShippingAddress(domain, orderId, newShippingAddress);
        this.successPopup();
      } catch (error) {
        handleError({ error });
      }

      this.setState({ processing: false });
    }
  }

  successPopup = () =>
    oneBtnPopupCreator({
      content: {
        main: `Shipping Address was changed`,
        iconPath: '/images/checkmark-green.svg',
      },
      btnConfig: 'CLOSE',
    });

  checkOnApprovedRole(array, role) {
    if (!array) {
      return false;
    }

    return array.find((item) => item === role);
  }

  checkOnFulfillmentStatus() {
    return get(this, 'props.selectedOrder.fulfillment_status') === 'fulfilled';
  }

  checkOnCancelStatus() {
    return !!get(this, 'props.selectedOrder.cancelled_at', null);
  }

  checkOnReadOnlyRight() {
    if (this.checkOnFulfillmentStatus() || this.checkOnCancelStatus() || !this.props.orderModificationsAreAllowed) {
      return true;
    }

    const { selectedShop } = this.props;
    const approvedRoles = get(
      this,
      [
        'props',
        'selectedToolDomains',
        'shopifyConfigs',
        selectedShop,
        'editShippingAddressConfig',
        'allowsEditingShippingAddress',
      ],
      ['specialist']
    );

    const specialistAllowsEdit = this.checkOnApprovedRole(approvedRoles, 'specialist') && this.props.isSpecialist;
    const expertAllowsEdit = this.checkOnApprovedRole(approvedRoles, 'expert') && !this.props.isSpecialist;

    return !(specialistAllowsEdit || expertAllowsEdit);
  }

  spinner = () => <MDSpinner color1={'#ffffff'} color2={'#ffffff'} color3={'#ffffff'} color4={'#ffffff'} size={10} />;

  getReasonLabel() {
    if (this.checkOnFulfillmentStatus()) {
      return 'The shipping address cannot be changed after the order has been fulfilled.';
    }

    if (this.checkOnCancelStatus()) {
      return 'The shipping address cannot be changed after the order has been canceled.';
    }

    return null;
  }

  renderReasonLabel() {
    if (this.checkOnFulfillmentStatus() || this.checkOnCancelStatus()) {
      return (
        <LabelWrapper>
          <span className="glyphicon glyphicon-info-sign" />
          <span>{this.getReasonLabel()}</span>
        </LabelWrapper>
      );
    }

    return null;
  }

  renderButton() {
    if (this.state.readOnlyRight) {
      return null;
    }

    const selectedShop = this.props.selectedShop;
    const { id: orderId } = this.props.selectedOrder;

    return (
      <ButtonWrapper>
        <Button
          type="button"
          onClick={this.updateShippingAddress.bind(this, selectedShop, orderId)}
          disabled={this.state.readOnlyRight || this.state.processing}
        >
          {this.state.processing ? <div>{this.spinner()}</div> : 'UPDATE'}
        </Button>
      </ButtonWrapper>
    );
  }

  render() {
    const { shipping_address: shippingAddress } = this.props.selectedOrder;
    if (!shippingAddress) {
      return (
        <ErrorMsg>
          <ErrorText>Shipping Address Unavailable</ErrorText>
        </ErrorMsg>
      );
    }

    const { name, address1, address2, city, province_code, province, zip, country_code } = this.state;

    return (
      <Page className="fs-block">
        {this.renderReasonLabel()}
        <ChangeAddressForm
          errors={this.state.errors}
          readOnly={this.state.readOnlyRight}
          formFields={this.formFields}
          update={this.update}
          data={{ name, address1, address2, city, province_code, province, zip, country_code }}
        />
        {this.renderButton()}
      </Page>
    );
  }
}

ShopifyShippingAddress.propTypes = {
  selectedOrder: PropTypes.shape({
    id: PropTypes.number.isRequired,
    shipping_address: PropTypes.shape({
      name: PropTypes.string,
      address1: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      province_code: PropTypes.string,
      province: PropTypes.string,
      zip: PropTypes.string,
    }),
  }).isRequired,
  selectedToolDomains: PropTypes.object,
  updateShippingAddress: PropTypes.func.isRequired,
  selectedShop: PropTypes.string.isRequired,
};

function mapStateToProps(state) {
  return {
    selectedOrder: getSelectedOrder(state),
    selectedShop: toolSelectors.getSelectedShop(state),
    selectedToolDomains: selectSelectedToolDomains(state),
    isSpecialist: isSpecialist(state),
    orderModificationsAreAllowed: getOrderModificationsAreAllowed(state),
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    async updateShippingAddress(shopName, orderId, newShippingAddress) {
      const payload = {
        shipping_address: newShippingAddress,
      };

      return dispatch(shopifyUpdateOrder(shopName, orderId, payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ShopifyShippingAddress);
